// Phase 1A Step 34 — Calculator mode toggle + stake input + chain-length slider.
// Extracted from Calculator.tsx for LOC ceiling.

type Mode = "protected" | "full";

interface CalculatorControlsProps {
  stake: number;
  setStake: (v: number) => void;
  weeks: number;
  setWeeks: (v: number) => void;
  takeout: number;
  symbol: string;
  mode: Mode;
  setMode: (m: Mode) => void;
}

export function CalculatorControls({
  stake,
  setStake,
  weeks,
  setWeeks,
  takeout,
  symbol,
  mode,
  setMode,
}: CalculatorControlsProps): JSX.Element {
  const tkPct = Math.round(takeout * 100);

  return (
    <>
      {/* Mode toggle */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 0,
          border: "1px solid var(--border-bright)",
          marginBottom: 22,
        }}
      >
        {(
          [
            { id: "protected", lbl: "Protected", sub: `${tkPct}% takeout / wk` },
            { id: "full", lbl: "Full Rollover", sub: "All-in compound" },
          ] as const
        ).map((m) => {
          const active = mode === m.id;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => setMode(m.id)}
              style={{
                padding: "12px 14px",
                background: active ? "rgba(212, 168, 67, 0.1)" : "transparent",
                border: 0,
                borderRight: m.id === "protected" ? "1px solid var(--border-bright)" : "0",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <div
                className="mono"
                style={{
                  fontSize: 11,
                  letterSpacing: "0.14em",
                  color: active ? "var(--accent)" : "var(--text-muted)",
                  textTransform: "uppercase",
                  fontWeight: 600,
                }}
              >
                {m.lbl}
              </div>
              <div
                className="mono"
                style={{
                  fontSize: 9,
                  letterSpacing: "0.12em",
                  color: active ? "var(--accent-dim)" : "var(--text-dim)",
                  textTransform: "uppercase",
                  marginTop: 3,
                }}
              >
                {m.sub}
              </div>
            </button>
          );
        })}
      </div>

      {/* Inputs row */}
      <div
        className="sz-l-calc-inputs"
        style={{
          display: "grid",
          gridTemplateColumns: "1.2fr 1fr",
          gap: 24,
          marginBottom: 24,
        }}
      >
        <div>
          <div className="eyebrow-muted" style={{ marginBottom: 10 }}>
            Your Stake
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 8,
              borderBottom: "1px solid var(--border-bright)",
              paddingBottom: 10,
            }}
          >
            <span className="mono" style={{ color: "var(--text-muted)", fontSize: 20 }}>
              {symbol}
            </span>
            <input
              type="number"
              className="stake-input"
              value={String(stake)}
              min={10}
              onChange={(e) => {
                const v = parseInt(e.target.value || "0", 10);
                setStake(Math.max(0, isNaN(v) ? 0 : v));
              }}
            />
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
            {[100, 300, 500, 1000].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setStake(v)}
                className="mono"
                style={{
                  padding: "5px 10px",
                  fontSize: 10,
                  letterSpacing: "0.08em",
                  background: stake === v ? "rgba(212,168,67,0.12)" : "transparent",
                  color: stake === v ? "var(--accent)" : "var(--text-muted)",
                  border: `1px solid ${stake === v ? "var(--accent-dim)" : "var(--border-bright)"}`,
                  cursor: "pointer",
                  transition: "all .15s",
                }}
              >
                {v.toLocaleString()}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div
            className="eyebrow-muted"
            style={{ marginBottom: 10, display: "flex", justifyContent: "space-between" }}
          >
            <span>Chain Length</span>
            <span
              className="mono"
              style={{
                color: "var(--accent)",
                letterSpacing: "0.04em",
                textTransform: "none",
              }}
            >
              {weeks} wk
            </span>
          </div>
          <input
            type="range"
            min={3}
            max={12}
            value={weeks}
            onChange={(e) => setWeeks(parseInt(e.target.value, 10))}
            style={{ width: "100%", accentColor: "var(--accent)", marginTop: 6 }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontFamily: "var(--mono)",
              fontSize: 10,
              color: "var(--text-dim)",
              marginTop: 6,
              letterSpacing: "0.06em",
            }}
          >
            <span>3</span>
            <span>6</span>
            <span>9</span>
            <span>12</span>
          </div>
        </div>
      </div>
    </>
  );
}
